import { Body, Box, Icon } from '@hover/blueprint';
import type { IconProp } from '@hover/icons';
import React, { forwardRef } from 'react';
import { Trans } from 'react-i18next';

import { getSocialOptions } from 'src/modules/app/components/Layout/Footer/Social/SocialLinkProps';
import { FullWidthContainer } from 'src/modules/pro/components/FullWidthContainer';
import { LinkExternal } from 'src/shared/components';
import type { LinkExternalProps } from 'src/shared/components/LinkExternal';
import { useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { External } from 'src/shared/route';

export const mobileFooterHeight = 360;

interface iWindow {
  OneTrust: {
    ToggleInfoDisplay: () => void;
  };
}

const FooterLink = (props: LinkExternalProps) => (
  <LinkExternal
    _active={{ color: 'inherit', textDecorationColor: 'inherit' }}
    color="inherit"
    fontWeight={200}
    size="footnote"
    {...props}
  />
);

export const Footer = forwardRef<HTMLDivElement>((_, ref) => {
  const t = useTranslate(TranslationNamespace.LEAD_LANDING_PAGE);

  const handleCookieSettingsClick = () => {
    (window as unknown as iWindow).OneTrust.ToggleInfoDisplay();
  };

  return (
    <FullWidthContainer
      borderTopColor="neutral.200"
      borderTopStyle="solid"
      borderTopWidth="1px"
      paddingX={{ base: 200, tablet: 800 }}
    >
      <Box
        flexDirection={{ base: 'column', tablet: 'row' }}
        h={{ base: mobileFooterHeight, tablet: 1 }}
        justifyContent="space-between"
        py={{ base: 500, tablet: 600 }}
        ref={ref}
        w={1}
      >
        <Box
          alignItems={{ base: 'flex-start', tablet: 'center' }}
          flexDirection={{ base: 'column', tablet: 'row' }}
          justifyContent={{ base: 'space-between', tablet: 'flex-start' }}
        >
          <Body fontWeight={700} mx={400} size="footnote" whiteSpace="nowrap">
            {t('footer.poweredByHover')}
          </Body>
          <Body mx={400} size="footnote" whiteSpace="nowrap">
            {t('footer.copyRight', { year: new Date().getFullYear() })}
          </Body>
          <Body mx={400} size="footnote" whiteSpace="nowrap">
            <Trans
              components={{
                PrivacyPolicyLink: (
                  <FooterLink href={External.PRIVACY_POLICY.path} />
                ),
                TermsOfUSeLink: (
                  <FooterLink href={External.TERMS_OF_SERVICE.path} />
                ),
              }}
              i18nKey="footer.privacyAndTermsOfUse"
              t={t}
            />
          </Body>
          <FooterLink
            as="button"
            cursor="pointer"
            mx={400}
            my={300}
            onClick={handleCookieSettingsClick}
            size="footnote"
            whiteSpace="nowrap"
          >
            {t('footer.cookieSettings')}
          </FooterLink>
        </Box>
        <Box>
          {getSocialOptions(t).map(({ label, icon, path, color, fill }) => (
            <LinkExternal color={color} href={path} key={label} mx={200}>
              <Icon fill={fill} icon={icon as IconProp} size="medium" />
            </LinkExternal>
          ))}
        </Box>
      </Box>
    </FullWidthContainer>
  );
});
